import { __rest, __assign } from '../../../node_modules/tslib/tslib.es6.js';
import { styled, useTheme, Box } from '@mui/material';
import React from 'react';
import brandclub_colors from '../../../colors/brandclubColors.js';
import { CampaignType } from '../../../types/Campaign.js';
import '../../../types/filters.js';
import '../../../types/product.js';
import '../../../types/RewardAmountType.js';
import { getImageAssetUrl } from '../../../utils/assetUtils.js';
import { useOpticalFontCenter } from '../../../utils/opticalFontCenter.js';
import { formatMoney } from '../../../utils/stringUtils.js';

var StyledContainer = styled('div', {
    shouldForwardProp: function (prop) { return !['rewardCampaignType', 'clickable'].includes(prop.toString()); },
})(function (_a) {
    var clickable = _a.clickable, theme = _a.theme;
    return ({
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        columnGap: 7,
        '*': {
            mixBlendMode: 'unset !important',
        },
        '.reward-pill': {
            textWrap: 'nowrap',
            borderRadius: 25,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0,
            display: 'flex',
            alignItems: 'center',
            ':hover': {
                cursor: clickable ? 'pointer' : 'default',
            },
        },
        '.rewardPercent-text': {
            color: theme.palette.primary.main,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.24,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
        },
    });
});
// default styles are from pdp buy-box
var getConfigByRewardCampaignType = function (rewardCampaignType, customPillColorPurchase, customPillColorPurchaseText, customPillColorSurvey, customPillColorSurveyText, customPillColorContent, customPillColorContentText, customPillColorAffiliate, customPillColorAffiliateText, customPillColorDailyDeal, customPillColorDailyDealText, customPillColorDefault, customPillColorDefaultText) {
    switch (rewardCampaignType) {
        case CampaignType.DailyDealsRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('instant-reward-icon.webp'),
                    alt: 'instant-reward-icon',
                },
                rewardTypeDisplay: 'instant',
                containerStyle: {
                    color: customPillColorDailyDealText ? customPillColorDailyDealText : brandclub_colors.orange_EC792E,
                    backgroundColor: customPillColorDailyDeal ? customPillColorDailyDeal : brandclub_colors.orange_EC792E_30,
                },
            };
        case CampaignType.PurchaseRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('purchase-reward-icon.webp'),
                    alt: 'purchase-reward-icon',
                },
                rewardTypeDisplay: 'purchase',
                containerStyle: {
                    color: customPillColorPurchaseText ? customPillColorPurchaseText : brandclub_colors.magenta_A22272,
                    backgroundColor: customPillColorPurchase ? customPillColorPurchase : brandclub_colors.magenta_A22272_30,
                },
            };
        case CampaignType.AffiliateRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('affiliate-reward-icon.webp'),
                    alt: 'affiliate-reward-icon',
                },
                rewardTypeDisplay: '',
                containerStyle: {
                    color: customPillColorAffiliateText ? customPillColorAffiliateText : brandclub_colors.teal_16949B,
                    backgroundColor: customPillColorAffiliate ? customPillColorAffiliate : brandclub_colors.teal_16949B_20,
                },
            };
        case CampaignType.ContentRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('content-reward-icon.webp'),
                    alt: 'content-reward-icon',
                },
                rewardTypeDisplay: '',
                containerStyle: {
                    color: customPillColorContentText ? customPillColorContentText : brandclub_colors.magenta_A22272,
                    backgroundColor: customPillColorContent ? customPillColorContent : brandclub_colors.magenta_A22272_30,
                },
            };
        case CampaignType.SurveyRewardCampaign:
            return {
                iconImgProps: {
                    src: getImageAssetUrl('survey-reward-icon.webp'),
                    alt: 'survey-reward-icon',
                },
                rewardTypeDisplay: '',
                containerStyle: {
                    color: customPillColorSurveyText ? customPillColorSurveyText : brandclub_colors.magenta_A22272,
                    backgroundColor: customPillColorSurvey ? customPillColorSurvey : brandclub_colors.magenta_A22272_30,
                },
            };
        default:
            return {
                iconImgProps: {
                    src: undefined,
                    alt: 'affiliate-reward-icon',
                },
                rewardTypeDisplay: '',
                containerStyle: {
                    color: customPillColorDefaultText ? customPillColorDefaultText : brandclub_colors.teal_16949B,
                    backgroundColor: customPillColorDefault ? customPillColorDefault : brandclub_colors.teal_16949B_20,
                },
            };
    }
};
var RewardPillWithText = function (_a) {
    var rewardAmount = _a.rewardAmount, rewardCampaignType = _a.rewardCampaignType, rewardPercent = _a.rewardPercent, rewardPercentText = _a.rewardPercentText, rewardPercentTextOverride = _a.rewardPercentTextOverride, _b = _a.containerStyle, containerStyleOverride = _b === void 0 ? {} : _b, _c = _a.pillContainerStyle, pillContainerStyle = _c === void 0 ? {} : _c, _d = _a.baseFontSize, baseFontSize = _d === void 0 ? 14 : _d, onPillClick = _a.onPillClick, className = _a.className;
    var theme = useTheme();
    var centerFontElements = useOpticalFontCenter();
    var customPillColor = theme === null || theme === void 0 ? void 0 : theme.customRewardPillColor;
    var _e = getConfigByRewardCampaignType(rewardCampaignType, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.purchase, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.purchaseText, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.survey, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.surveyText, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.content, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.contentText, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.affiliate, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.affiliateText, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.dailyDeal, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.dailyDealText, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.default, customPillColor === null || customPillColor === void 0 ? void 0 : customPillColor.defaultText), iconImgProps = _e.iconImgProps, rewardTypeDisplay = _e.rewardTypeDisplay, containerStyle = _e.containerStyle;
    var restIconImgProps = __rest(iconImgProps, []);
    var pillDefaultPadding = {
        paddingTop: "".concat(baseFontSize / 3, "px"),
        paddingLeft: "".concat(baseFontSize * 0.95, "px"),
        paddingBottom: "".concat(baseFontSize / 3, "px"),
        paddingRight: "".concat(baseFontSize * 0.95, "px"),
    };
    var subfixText = rewardPercentTextOverride
        ? rewardPercentTextOverride
        : rewardPercent
            ? Math.round(rewardPercent) + '% ' + (rewardPercentText !== null && rewardPercentText !== void 0 ? rewardPercentText : "".concat(rewardTypeDisplay, " reward"))
            : null;
    return (React.createElement(StyledContainer, { rewardCampaignType: rewardCampaignType, style: __assign({ display: rewardAmount ? 'flex' : 'none' }, containerStyleOverride), clickable: !!onPillClick, className: className },
        React.createElement(Box, { sx: { borderRadius: '999px', backgroundColor: '#fff', display: 'flex' } },
            React.createElement(Box, { className: "reward-pill", sx: __assign(__assign(__assign(__assign({}, containerStyle), pillDefaultPadding), pillContainerStyle), { fontWeight: 600, fontFamily: 'inherit' }), onClick: onPillClick },
                iconImgProps.src ? (React.createElement("img", __assign({}, restIconImgProps, { style: {
                        maxWidth: baseFontSize,
                        maxHeight: baseFontSize,
                        objectFit: 'contain',
                        marginRight: baseFontSize * 0.416,
                        verticalAlign: 'middle',
                    } }))) : null,
                React.createElement(Box, { component: "span", ref: function (ref) {
                        ref && centerFontElements(ref);
                    }, sx: {
                        margin: 0,
                        padding: 0,
                    } }, formatMoney(rewardAmount)))),
        subfixText ? (React.createElement("div", { className: "rewardPercent-text", ref: function (ref) {
                ref && centerFontElements(ref);
            }, style: {
                fontWeight: 600,
                fontFamily: 'inherit',
            } }, subfixText)) : null));
};

export { RewardPillWithText };
//# sourceMappingURL=RewardPillWithText.js.map
